// src/services/transactionFetcher/bscTransactionFetcher.js
/* global BigInt */
import { TRANSACTION_TYPES } from '../../contexts/TransactionContext';
import { standardizeTransactionFormat } from './transactionFormatUtils';
import secureFetch from '../../utils/secureFetch';

/**
 * Detect transaction type based on method, value, and addresses
 * @param {Object} tx - Transaction data from the Moralis API
 * @param {string} walletAddress - Address of the user's wallet
 * @returns {string} - Transaction type from TRANSACTION_TYPES
 */
const detectTransactionType = (tx, walletAddress) => {
  const walletAddressLower = walletAddress.toLowerCase();
  const fromIsWallet = tx.from_address && tx.from_address.toLowerCase() === walletAddressLower;
  const toIsWallet = tx.to_address && tx.to_address.toLowerCase() === walletAddressLower;
  
  // Check for method names in decoded_call
  if (tx.decoded_call) {
    const methodName = tx.decoded_call.method || '';
    
    // Detect approve transactions
    if (methodName.toLowerCase().includes('approve')) {
      return TRANSACTION_TYPES.APPROVE;
    }
    
    // Detect swap transactions
    if (methodName.toLowerCase().includes('swap')) {
      return TRANSACTION_TYPES.SWAP;
    }
  }
  
  // If the transaction is from the wallet but not to the wallet, it's a send
  if (fromIsWallet && !toIsWallet && tx.value !== '0') {
    return TRANSACTION_TYPES.SEND;
  }
  
  // If the transaction is to the wallet but not from the wallet, it's a receive
  if (!fromIsWallet && toIsWallet && tx.value !== '0') {
    return TRANSACTION_TYPES.RECEIVE;
  }
  
  // If there's decoded call data, it's a contract interaction
  if (tx.decoded_call) {
    return TRANSACTION_TYPES.CONTRACT;
  }
  
  // Default for any other transaction
  return TRANSACTION_TYPES.UNKNOWN;
};

/**
 * Fetch transactions for a BSC wallet using the Moralis API with verbose decoding.
 * Transforms the response to match the expected format in TransactionContext.
 *
 * @param {string} walletAddress - The wallet address to query
 * @param {Object} options - Additional options like limit, nextToken, etc.
 * @param {Object} chainConfig - Chain configuration from supportedChains
 * @returns {Promise<{transactions: Array, hasMore: boolean, nextToken: string}>} - Transactions and pagination info
 */
export const fetchTransactions = async (walletAddress, options = {}, chainConfig = null) => {
  try {
    const API_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJub25jZSI6ImMxZmIxMzNiLThjMWEtNDYzMi05YjU5LWNhNWI0YmMzYjIwZiIsIm9yZ0lkIjoiNDI1MTY2IiwidXNlcklkIjoiNDM3Mjg3IiwidHlwZSI6IlBST0pFQ1QiLCJ0eXBlSWQiOiJhMWQ3YjZkMC03NTc0LTQ1ZDEtOTZiNC0wMGM2NjU5NmZlNGQiLCJpYXQiOjE3NDU4NzkwMTYsImV4cCI6NDkwMTYzOTAxNn0.9Gl8TWkknMJ6ld3YPJ14XoskdXYBksytD7O9uxEJ0Vk"; 
    
    const { limit = 50, nextToken = null } = options;
    
    // Ensure we have a valid wallet address
    if (!walletAddress || walletAddress.length < 40) {
      console.error("Invalid wallet address:", walletAddress);
      return { transactions: [], hasMore: false, nextToken: null };
    }
    
    // Using the verbose endpoint to get decoded transactions
    // This provides method names and detailed transaction data
    let url = `https://deep-index.moralis.io/api/v2.2/${walletAddress}/verbose?chain=bsc&limit=${limit}`;
    
    // Add cursor if provided for pagination
    if (nextToken) {
      url += `&cursor=${nextToken}`;
    }
    
    const response = await secureFetch(url, {
      headers: {
        "Accept": "application/json",
        "X-API-Key": API_KEY
      }
    });
    
    if (!response.ok) {
      console.error(`API error: ${response.status} - ${response.statusText}`);
      return { transactions: [], hasMore: false, nextToken: null };
    }
    
    const data = await response.json();
    
    // Check if the response has the expected structure
    if (!data || !Array.isArray(data.result)) {
      console.error("Unexpected API response format:", data);
      return { transactions: [], hasMore: false, nextToken: null };
    }
    
    // Transform the data to match the expected format in TransactionContext
    const mappedTransactions = data.result.map(tx => {
      // Get native token info from chain config or use defaults
      const tokenSymbol = chainConfig?.nativeTokenSymbol || 'BNB';
      const tokenDecimal = chainConfig?.nativeTokenDecimals || 18;
      const network = chainConfig?.key || 'bsc';
      
      // Determine transaction type based on decoded data
      const type = detectTransactionType(tx, walletAddress);
      
      // Calculate fee (gas_used * gas_price)
      let fee = '0';
      try {
        if (tx.receipt_gas_used && tx.gas_price) {
          fee = (BigInt(tx.receipt_gas_used) * BigInt(tx.gas_price)).toString();
        }
      } catch (e) {
        console.error("Error calculating fee:", e);
      }
      
      // Get method name from decoded call or default to empty string
      let methodName = '';
      if (tx.decoded_call && tx.decoded_call.method) {
        methodName = tx.decoded_call.method;
      }
      
      return {
        hash: tx.hash,
        type,
        from: tx.from_address || '',
        to: tx.to_address || '',
        value: tx.value || '0',
        timestamp: new Date(tx.block_timestamp).getTime(),
        status: tx.receipt_status === '1' ? 'success' : 'failed',
        tokenSymbol,
        tokenDecimal,
        fee,
        network,
        data: tx.input && tx.input.length > 10 ? tx.input.substring(0, 10) : '',
        methodName,
        explorerUrl: `https://bscscan.com/tx/${tx.hash}`
      };
    });
    
    // Filter out any null mappings
    const transactions = mappedTransactions.filter(tx => tx !== null);
    
    // Get the pagination cursor from the API response
    const hasMore = !!data.cursor;
    const nextPageToken = data.cursor || null;
    
    // Return transactions along with pagination information
    return {
      transactions: standardizeTransactionFormat(transactions),
      hasMore,
      nextToken: nextPageToken
    };
  } catch (error) {
    console.error("Error fetching BSC transactions:", error);
    return { transactions: [], hasMore: false, nextToken: null };
  }
};